const ContentWidgetType = {
	List: 0,
	Dialog: 1,
	Wizard: 2,
	ObjectDetails: 3,
	Base: 4,
	LandingPage: 5,
	CustomWidget: 6,
	DocumentTemplate: 7,
	Widget: 8,
};

enum ContentWidgetViewType {
	Grid = 0,
	List = 1,
	Tile = 2,
	ObjectDetails = 3,
	DialogPage = 4,
	WizardPage = 5,
	Dashboard = 6,
	NonVisual = 7,
	Calendar = 8,
	ResultWizardPage = 10,
	CustomSearchForm = 11,
	DocumentTemplate = 12,
	Board = 16,
	WidgetRuntime = 17,
	WidgetConfiguration = 18,
}

enum ContentWidgetDialogAction {
	Unknown = 0,
	New = 1,
	Edit = 2,
	Preview = 3,
}

enum ContentWidgetDialogPurpose {
	CreateAndEdit = 0,
	CreateOnly = 1,
	EditOnly = 2,
}

enum SourceLoadingStatus {
	NOT_SET = -1,
	INITIAL = 0,
	LOADING = 1,
	LOADED = 2,
	REJECTED = 3,
	LOAD_REQUESTED = 4,
}

enum SourceParameterType {
	FromUri = 0,
	FromBody = 1,
	Unknown = 2,
}

enum PropertyEditorType {
	String = 0,
	Boolean = 1,
	Integer = 2,
	StylePicker = 3,
	Enumeration = 4,
	Localizable = 5,
	Bindable = 6,
	Image = 7,
	Unit = 8,
	ComplexData = 9,
	Schema = 10,
	Text = 11,
	ObjectPicker = 12,
	DescriptorStylesPicker = 13,
	LayoutGrid = 15,
	ExpressionFilter = 26,
}

enum EditorBindingType {
	OneWay = 0,
	TwoWay = 1,
}

type TemplatePropertyValueType = 'model' | 'local' | 'tabs' | 'style' | 'img' | 'columns' | 'color' | 'actions' | 'layoutGrid' | 'filterExpression';

class MxTemplatePropertyValueTypes implements Record<TemplatePropertyValueType, { id: number; type: string }> {
	public readonly model = {
		id: 1,
		type: 'mx.SolutionBuilderAgent.DataModelProperty',
	};
	public readonly local = {
		id: 2,
		type: 'mx.SolutionBuilderAgent.LocalizableResource',
	};
	public readonly tabs = {
		id: 3,
		type: 'mx.designer.TabulatedContainerDescriptor',
	};
	public readonly style = {
		id: 4,
		type: 'mx.designer.StylesItem',
	};
	public readonly img = {
		id: 5,
		type: 'mx.designer.ImageBindableValue',
	};
	public readonly columns = {
		id: 6,
		type: 'mx.designer.GridColumnDefinitionDescriptor',
	};
	public readonly color = {
		id: 7,
		type: 'mx.designer.ColorItem',
	};
	public readonly actions = {
		id: 8,
		type: 'mx.designer.DialogActionDefinitionDescriptor',
	};
	public readonly layoutGrid = {
		id: 15,
		type: 'mx.designer.LayoutGridCell',
	};
	public readonly filterExpression = {
		id: 16,
		type: 'mx.designer.FilterExpression',
	};

	_is(data: { __type: number }, name: TemplatePropertyValueType) {
		return data && data.__type && data.__type === this[name].id;
	}

	is(data: { __type: number }, name: TemplatePropertyValueType) {
		return this.hasOwnProperty(name) && this._is(data, name);
	}

	isModel(data: { __type: number }) {
		return this._is(data, 'model');
	}

	isLocalization(data: { __type: number }) {
		return this._is(data, 'local');
	}

	isStyle(data: { __type: number }) {
		return this._is(data, 'style');
	}

	isImg(data: { __type: number }) {
		return this._is(data, 'img');
	}

	isColor(data: { __type: number }) {
		return this._is(data, 'color');
	}

	isLayoutGrid(data: { __type: number }) {
		return this._is(data, 'layoutGrid');
	}

	isFilterExpression(data: { __type: number }) {
		return this._is(data, 'filterExpression');
	}
}

enum DetailsViewSelectionMode {
	None = 1,
	Single = 2,
	Multi = 3,
}

enum NavigationItemImplementation {
	SearchPage = 'D',
	LandingPage = 'L',
	CustomController = 'C',
	ExternalUrl = 'E',
	Dashboard = 'W',
	Default = 'N',
}

enum StructureNavigationItemImplementation {
	DataDefinition = 'D',
	ServiceOperation = 'S',
}

enum WizardMode {
	Default = 0,
	New = 1,
	Edit = 2,
}

enum LanguageGroupFilter {
	UI = 0,
	Email = 1,
	All = 2,
}

enum AttachmentsViewMode {
	Default = 'default',
	List = 'list',
	Tiles = 'tiles',
}

enum SupportedOperations {
	Undefined = 0,
	ReadObject = 1,
	ReadObjects = 2,
	ReadObjectsByODataQuery = 4,
	UpdateObject = 8,
	InsertObject = 16,
	DeleteObject = 32,
}

enum PreprocessorType {
	Sorting = 'sorting',
	Paging = 'paging',
	Search = 'search',
	DateRange = 'date_range',
	Structure = 'structure',
	QuickFilter = 'quick_filter',
	ColumnDefinition = 'column_definition',
	ServiceRequestParams = 'service_request_params',
	Grouping = 'grouping',
	ArchivedData = 'archived_data',
}

enum GroupType {
	Basic = 'basic',
	DateRange = 'dateRange',
	GridSearch = 'gridSearch',
	Grouping = 'grouping',
	QueryBuilder = 'queryBuilder',
}

enum ListingType {
	grid = 0,
	list = 1,
	tiles = 2,
	calendar = 3,
	board = 4,
}

enum ClassTypes {
	commonClass = 9,
	customClass = 3,
	pickup = 4,
	simpleCiBase = 13,
	simpleCommonClass = 12,
}

enum DataTypes {
	StringType = 0,
	GuidType = 1,
	IntType = 2,
	DateType = 3,
	LongType = 4,
	FloatType = 5,
	BlobType = 6,
	VirtualRelationEnd = 7,
	TextType = 8,
	BoolType = 9,
	DecimalType = 10,
	ExpressionGuidType = 11,
	BitType = 12,
	VarBinaryType = 13,
	StringTypeShort = 14,
	Currency = 15,
	CurrencyCode = 16,
	Unknown = 200,
}

enum RelatedClassCardinality {
	one = 0,
	oneOrMany = 1,
	zeroOrOne = 2,
	zeroOrMany = 3,
}

enum FeatureTrackingType {
	none = 0,
	aiSearch = 4,
	search = 6,
	viewReport = 20,
	reportDrillDown = 21,
	aiAgentMessage = 30,
	humanAgentMessage = 31,
	widgetOpened = 32,
}

enum FeatureTrackingUseCases {
	none = 0,
	prevented = 1,
	withdrawnByUser = 2,
	helpToResolve = 3,
	traditionallyResolved = 4,
	reportingToolkit = 20,
	knowledgeDiscoveryForEndUsers = 30,
	globalSearch = 100,
	likeSearch = 101,
	dislikeSearch = 102,
	msTeamsAppUserLoginSuccess = 200,
	msTeamsAppUserLoginFailed = 201,
	msTeamsPushNotificationSent = 205,
}

const TemplatePropertyValueTypes = new MxTemplatePropertyValueTypes();

mx.SolutionBuilderAgent = mx.SolutionBuilderAgent || {};
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
Object.assign(mx.SolutionBuilderAgent, {
	ContentWidgetType,
	ContentWidgetViewType,
	ContentWidgetDialogAction,
	ContentWidgetDialogPurpose,
	TemplatePropertyValueTypes,
	SourceLoadingStatus,
	SourceParameterType,
	DetailsViewSelectionMode,
	NavigationItemImplementation,
	StructureNavigationItemImplementation,
	WizardMode,
	PropertyEditorType,
	LanguageGroupFilter,
	AttachmentsViewMode,
	SupportedOperations,
	PreprocessorType,
	ListingType,
	ClassTypes,
	DataTypes,
	RelatedClassCardinality,
	FeatureTrackingType,
	FeatureTrackingUseCases,
});

export {
	ContentWidgetType,
	ContentWidgetViewType,
	ContentWidgetDialogAction,
	ContentWidgetDialogPurpose,
	TemplatePropertyValueTypes,
	SourceLoadingStatus,
	SourceParameterType,
	DetailsViewSelectionMode,
	NavigationItemImplementation,
	StructureNavigationItemImplementation,
	WizardMode,
	PropertyEditorType,
	LanguageGroupFilter,
	AttachmentsViewMode,
	SupportedOperations,
	PreprocessorType,
	ListingType,
	ClassTypes,
	DataTypes,
	RelatedClassCardinality,
	GroupType,
	EditorBindingType,
	FeatureTrackingType,
	FeatureTrackingUseCases,
};
